<template lang="pug">
.c-help-question
  template(v-if="sent")
    p.c-help-question__thanks Thanks! We got your question and will get back to you.
  button.c-help-question__toggle(v-else-if="!open" type="button" @click="open = true")
    | {{ placeholder }}
  template(v-else)
    el-input(
      v-model="question"
      type="textarea"
      :rows="2"
      maxlength="2000"
      placeholder="Type your question…"
    )
    el-button.mt-2(
      size="small"
      :disabled="!question.trim()"
      :loading="sending"
      @click="send"
    ) Send question
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useRoute } from 'vue-router';
import { ElMessage } from 'element-plus';
import globalShared from '@/shared';
import { trackHelpEvent } from '@/graphs/gql';

export default defineComponent({
  name: 'HelpQuestionBox',
  props: {
    orgId: { type: String, default: null },
    taskId: { type: String, default: null },
    placeholder: { type: String, default: 'Still stuck? Ask us a question' },
    startOpen: { type: Boolean, default: false },
  },
  setup(props) {
    const { makeGraphRequest } = globalShared();
    const route = useRoute();
    const question = ref('');
    const sent = ref(false);
    const sending = ref(false);
    const open = ref(props.startOpen);
    // Unlike the other help signals, a question must not be lost: only thank the user once
    // the backend has stored it, otherwise keep the text so they can retry.
    async function send() {
      const text = question.value.trim();
      if (!text || !props.orgId || sending.value) return;
      const input: { [key: string]: any } = {
        eventType: 'QUESTION_ASKED', question: text, orgId: props.orgId, page: route.path,
      };
      if (props.taskId) input.taskId = props.taskId;
      sending.value = true;
      try {
        const response = await makeGraphRequest('mutation', trackHelpEvent, { input });
        if (response?.errors || !response?.data?.trackHelpEvent) {
          throw new Error(response?.errors?.[0]?.message);
        }
        sent.value = true;
      } catch (e) {
        ElMessage.error('Could not send your question. Please try again.');
      } finally {
        sending.value = false;
      }
    }
    return {
      question, sent, sending, open, send,
    };
  },
});
</script>

<style lang="scss" scoped>
.c-help-question {
  margin-top: 12px;
  &__thanks { margin: 0; font-size: 12px; color: #005c6b; }
  &__toggle {
    padding: 0;
    border: 0;
    background: none;
    font-size: 12px;
    color: #4349dd;
    text-decoration: underline;
    cursor: pointer;
  }
}
</style>
